import { HStack, Text } from '@talos/kyoko';
import styled from 'styled-components';
import { SIGNALS_DUAL_AXIS_MARGIN_LEFT } from '../core';
import type { FundingRateLegendItem } from './fundingRateLegendItems';

interface FundingRateLegendProps {
  items: FundingRateLegendItem[];
}

export function FundingRateLegend({ items }: FundingRateLegendProps) {
  if (items.length === 0) {
    return null;
  }

  return (
    <LegendRow data-testid="funding-rate-legend">
      {items.map(item => (
        <LegendChip key={item.id} data-testid={`funding-rate-legend-chip-${item.id}`}>
          {item.marker === 'dashed' ? <DashedRing $color={item.color} /> : <ColorDot $color={item.color} />}
          <Text size="fontSizeSmall">{item.label}</Text>
          <Text size="fontSizeSmall" color={item.isPositive ? 'colorTextPositive' : 'colorTextNegative'}>
            {item.value}
          </Text>
        </LegendChip>
      ))}
    </LegendRow>
  );
}

const LegendRow = styled(HStack)`
  position: absolute;
  top: ${({ theme }) => theme.spacing16}px;
  /* Clear the LHS y-axis margin (title + tick values) so chips sit inside the plot area. */
  left: ${SIGNALS_DUAL_AXIS_MARGIN_LEFT}px;
  right: ${({ theme }) => theme.spacing8}px;
  z-index: 1;
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: ${({ theme }) => theme.spacing8}px;
  pointer-events: none;
`;

const LegendChip = styled(HStack)`
  gap: ${({ theme }) => theme.spacing4}px;
  padding: ${({ theme }) => theme.spacing2}px ${({ theme }) => theme.spacing4}px;
  background: ${({ theme }) => theme.backgroundDropdown};
  border: 1px solid ${({ theme }) => theme.borderColorPopover};
  border-radius: ${({ theme }) => theme.borderRadiusDefault}px;
`;

const ColorDot = styled.span<{ $color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ $color }) => $color};
  flex-shrink: 0;
`;

const DashedRing = styled.span<{ $color: string }>`
  width: 9px;
  height: 9px;
  border-radius: 50%;
  border: 1px dashed ${({ $color }) => $color};
  flex-shrink: 0;
`;
